import React, {useState} from "react";
import TextareaAutosize from "react-textarea-autosize";
// local
import {InitialsBadge} from "components/InitialsBadge";
import Upload from "components/Upload";
import ProjectsSelect from "components/selects/ProjectsSelect";
import {ProjectLabel} from "components/ProjectLabel";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import ButtonDelete from "components/buttons/ButtonDelete";
import {IconPlane} from "components/icons";
import {LockValue} from "components/LockValue";
import {hasPermission} from "helpers/helper";
import {useUpload, useFileDrop} from "helpers/uploadHooks";
import {fileExtension} from "constants/file.consts";
import i18next from "i18next";
import cn from "classnames";
import {color} from "constants/color.consts";

const fileExtensions = [
  fileExtension.PDF,
  fileExtension.PNG,
  fileExtension.JPG,
  fileExtension.XLS,
  fileExtension.XLSX,
];
const dateOptions = {
  month: "short",
  day: "numeric",
  weekday: "long",
  hour: "numeric",
  minute: "numeric",
};

const MessagePanel = ({
  message,
  mode = "view",
  loading,
  placeholder,
  noAttachDocument,
  canDeleteMessage,
  projects,
  noSelectProjectValue,
  highlightNoSelectProjectValue,
  project,
  allProjectItem,
  onDelete,
  isMyMessage,
  sendMessage,
  permissionsMessage,
  permissionsFile,
  addClass,
}) => {
  const [text, setText] = useState("");
  const [projectId, setProjectId] = useState(null);
  const [files, setFiles] = useState(message?.MessageFiles ?? []);
  const language = i18next.language;

  const upload = useUpload({
    files,
    onChange: setFiles,
    disabledUpload: loading,
    customFileTypes: fileExtensions,
    customFileSize: 102400,
    maxFilesCount: 50,
    multiple: true,
  });
  // the button is locked without permissionsFile, so is the drop area
  const canAttachFiles =
    !noAttachDocument && (!permissionsFile || hasPermission(permissionsFile));
  const {dropProps, dropClassName} = useFileDrop({
    onFiles: upload.addFiles,
    disabled: !canAttachFiles || loading,
    fileTypes: fileExtensions,
  });

  const hasFiles = !!files.length;
  const disabledSendButton =
    !(text || hasFiles) || loading || (!!projects?.length ? !projectId : false);
  const isMineProject =
    !!project && project.Members?.map((member) => member.id).includes(message.User?.id);

  const clearAndSend = () => {
    if (!!permissionsMessage && !hasPermission(permissionsMessage)) return;
    sendMessage(text, files, projectId);
    setText("");
    setFiles([]);
    setProjectId(null);
  };

  return (
    <div className={cn("message-panel_wrapper", {[addClass]: !!addClass})}>
      <InitialsBadge
        firstName={message.User?.firstName}
        lastName={message.User?.lastName}
        large
        isCurrentUser={isMyMessage}
        addClass="mr-4 message-panel"
      />
      {mode === "create" && (
        <div
          className={cn("message-panel_create-mode_wrapper", dropClassName)}
          {...dropProps}
        >
          <TextareaAutosize
            maxRows="4"
            className="textarea"
            name="messageText"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
          />
          <div className="tools __normal __nonecase">
            {!noAttachDocument && (
              <Upload
                variant="attachment"
                upload={upload}
                uploadPermissions={permissionsFile}
                needRemoveConfirm={false}
              />
            )}

            {!!projects?.length && (
              <ProjectsSelect
                list={!!allProjectItem ? [allProjectItem, ...projects] : projects}
                onChange={setProjectId}
                noBorder
                selectedValue={projectId}
                highlightIfNoSelectValue={highlightNoSelectProjectValue}
                noSelectValue={noSelectProjectValue}
                highlightMyProjectsId={message.User?.id}
                addClass="mt-2 pr-0 mla mr-2"
              />
            )}
            <LockValue
              canPermissions={permissionsMessage}
              needLabel
              needShowModalToSubscribe
              noLockIcon
              dimmedWhenLocked
              addClassWrapper="mla"
              addClassIcon={`mb-2 ${disabledSendButton ? "ml-0" : ""}`}
            >
              <ButtonTransparent
                disabled={disabledSendButton}
                icon={<IconPlane color={disabledSendButton && color.midGray} />}
                circular
                disabledTransparent
                noBorder
                addClass={`mb-2 ${!!projects?.length ? "" : "mla"}`}
                onClick={clearAndSend}
              />
            </LockValue>
          </div>
        </div>
      )}
      {mode === "view" && (
        <React.Fragment>
          <div className={cn("message-panel_view-mode_wrapper", {"pb-1": hasFiles})}>
            <div className="message-panel_view-mode_date df">
              {new Date(message?.createdAt).toLocaleString(language, dateOptions)}
              {!!project && (
                <ProjectLabel
                  name={project.name}
                  id={project.id}
                  color={!isMineProject && "discreteText"}
                  width="400"
                  addClass="mla"
                />
              )}
            </div>
            <div className="pt-2">{message.message}</div>
            <Upload
              variant="overview"
              files={files.map((file) => file.File)}
              viewMode
              wrapperClass="ml-[-10]"
              fileListClass="df-row-wrap-center"
            />
          </div>
          {canDeleteMessage && (
            <ButtonDelete activeOnHover addClass="ml-4 prl-0" onClick={onDelete} />
          )}
        </React.Fragment>
      )}
    </div>
  );
};

export default MessagePanel;
